import { APP_TRIAL_URL, SITE_URL } from "@/lib/links";
import type { Locale } from "@/lib/i18n";

export const SITE_NAME = "Samvolt Intelligence";

/** Primary pitch: full-system AI control — not spot-price-only. */
export const SITE_DESCRIPTION =
  "AI-styrning för sol, batteri och växelriktare. Samvolt Intelligence förutser, optimerar och styr hela anläggningen i realtid – stöd för Solinteg och Sunwoda. För privatpersoner, installatörer och sälj.";

export const SITE_TITLE =
  "AI-styrning för sol & batteri | Solinteg & Sunwoda | Samvolt Intelligence";

export const SITE_DESCRIPTION_EN =
  "AI control for solar, battery and inverter. Samvolt Intelligence predicts, optimizes and controls your entire installation in real time – supports Solinteg and Sunwoda. For homeowners, installers and sales.";

export const SITE_TITLE_EN =
  "AI control for solar & battery | Solinteg & Sunwoda | Samvolt Intelligence";

export const SITE_KEYWORDS_EN = [
  "AI energy management",
  "AI control solar panels",
  "AI battery control",
  "AI inverter control",
  "AI control Sunwoda",
  "AI control Solinteg",
  "intelligent energy management",
  "automatic energy optimization",
  "energy optimization",
  "smart energy management",
  "solar energy",
  "solar panels",
  "inverter",
  "battery storage",
  "home battery",
  "Solinteg",
  "Sunwoda",
  "solar self-consumption",
  "power peaks",
  "power tariff",
  "home energy management",
  "solar aftermarket",
  "remote solar monitoring",
  "AI for installers",
  "Samvolt",
  "Samvolt Intelligence",
];

export const SITE_KEYWORDS = [
  "AI-styrning",
  "AI styrning solceller",
  "AI styrning batteri",
  "AI styrning växelriktare",
  "AI styrning Sunwoda",
  "AI styrning Solinteg",
  "intelligent energistyrning",
  "automatisk energioptimering",
  "energioptimering",
  "smart energistyrning",
  "solel",
  "solceller",
  "växelriktare",
  "batterilager",
  "hembatteri",
  "Solinteg",
  "Sunwoda",
  "egenanvändning solel",
  "effekttoppar",
  "effekttariff",
  "energihantering hem",
  "eftermarknad solceller",
  "fjärrövervakning solceller",
  "AI för installatörer",
  "Samvolt",
  "Samvolt Intelligence",
];

export type SeoPageContent = {
  slug: string;
  title: string;
  description: string;
  h1: string;
  h1Accent?: string;
  lead: string;
  sections: { heading: string; body: string }[];
  faqs: { q: string; a: string }[];
  audience: "private" | "both";
};

export const SEO_PAGES: SeoPageContent[] = [
  {
    slug: "ai-styrning-sunwoda",
    title: "AI-styrning för Sunwoda – växelriktare & batteri",
    description:
      "AI-styrning för din Sunwoda. Samvolt Intelligence kopplar upp växelriktare och batteri och styr anläggningen i realtid – prognos, optimering och automatisk drift.",
    h1: "AI-styrning för din",
    h1Accent: "Sunwoda",
    lead: "Har du en Sunwoda-växelriktare eller batteri? Samvolt Intelligence ansluter till din anläggning och styr den intelligent – så att sol, batteri och förbrukning samspelar utan att du behöver styra manuellt.",
    sections: [
      {
        heading: "Mer än en enkel regel",
        body: "Intelligence förutser produktion och last, väger batterinivå, elpris och effekttoppar, och justerar driften i realtid. Målet är att hela anläggningen arbetar smartare – inte att bara följa en enskild signal.",
      },
      {
        heading: "För dig med Sunwoda hemma",
        body: "Logga in med BankID, registrera anläggningen och koppla din Sunwoda. Därefter sköter Intelligence schemaläggning och finjustering automatiskt, dygnet runt.",
      },
      {
        heading: "För installatörer och sälj",
        body: "Erbjud kunderna AI-styrning som tillägg till Sunwoda-installationer. Övervaka portföljen, minska support och skapa naturlig merförsäljning när anläggningar behöver utbyggnad.",
      },
    ],
    faqs: [
      {
        q: "Fungerar Samvolt Intelligence med Sunwoda?",
        a: "Ja. Du kan ansluta Sunwoda-växelriktare och batteri till Samvolt Intelligence för AI-styrning och övervakning i realtid.",
      },
      {
        q: "Behöver jag byta hårdvara?",
        a: "Nej. Intelligence kopplas till din befintliga Sunwoda-anläggning – ingen ny växelriktare krävs i normalfallet.",
      },
      {
        q: "Vad styr AI:n egentligen?",
        a: "Systemet planerar och finjusterar laddning, urladdning och energiflöden utifrån solprognos, förbrukning, batterinivå, elpris och effekttoppar – så att anläggningen optimeras som helhet.",
      },
    ],
    audience: "both",
  },
  {
    slug: "ai-styrning-solinteg",
    title: "AI-styrning för Solinteg – växelriktare & batteri",
    description:
      "AI-styrning för din Solinteg. Samvolt Intelligence kopplar upp växelriktare och batteri och styr anläggningen i realtid – prognos, optimering och automatisk drift.",
    h1: "AI-styrning för din",
    h1Accent: "Solinteg",
    lead: "Har du en Solinteg-växelriktare eller batteri? Samvolt Intelligence ansluter till din anläggning och styr den intelligent – så att sol, batteri och förbrukning samspelar utan manuellt work.",
    sections: [
      {
        heading: "Helhetsoptimering för Solinteg",
        body: "Intelligence läser av anläggningen och styr energiflöden automatiskt. Solprognos, last, batteri, elpris och effekt gränsas in i samma beslut – inte som isolerade knappar.",
      },
      {
        heading: "För privatpersoner",
        body: "Kom igång på några minuter med BankID. Koppla din Solinteg och låt Intelligence sköta driften medan du följer produktion, förbrukning och resultat i appen.",
      },
      {
        heading: "För installatörer och sälj",
        body: "Ge Solinteg-kunder AI-styrning som standard eller tillval. Fjärrövervaka anläggningar, fånga merförsäljning och bygg en starkare eftermarknad.",
      },
    ],
    faqs: [
      {
        q: "Fungerar Samvolt Intelligence med Solinteg?",
        a: "Ja. Solinteg-växelriktare och batterier stöds för anslutning, övervakning och AI-styrning via Samvolt Intelligence.",
      },
      {
        q: "Kan jag styra mitt Solinteg-batteri med AI?",
        a: "Ja. Intelligence planerar och justerar batteridriften utifrån hela anläggningens behov – automatiskt och i realtid.",
      },
      {
        q: "Är det byggt för svenska installationer?",
        a: "Ja. Plattformen är utvecklad i Sverige med fokus på nordiska anläggningar, säkerhet och dataskydd.",
      },
    ],
    audience: "both",
  },
  {
    slug: "ai-styrning-vaxelriktare",
    title: "AI-styrning för växelriktare | Samvolt Intelligence",
    description:
      "AI-styrning för din växelriktare. Koppla sol och batteri till Samvolt Intelligence – intelligent energistyrning i realtid. Stöd för Solinteg och Sunwoda.",
    h1: "AI-styrning för din",
    h1Accent: "växelriktare",
    lead: "Din växelriktare är navet i anläggningen. Samvolt Intelligence kopplas in där och styr sol, batteri och energiflöden intelligent – så att varje kilowattimme används smartare.",
    sections: [
      {
        heading: "Från växelriktare till intelligent anläggning",
        body: "Vi ansluter till din befintliga växelriktare och tar över den dagliga optimeringen: prognoser, batteribeslut, egenanvändning och effekt – samlat i en plattform.",
      },
      {
        heading: "Stöd för Solinteg och Sunwoda",
        body: "Solinteg och Sunwoda stöds i dag.",
      },
      {
        heading: "Privat och proffs",
        body: "Privatpersoner får automatisk drift och tydlig app. Installatörer och sälj får ett erbjudande att sätta efter installationen – med portföljöversikt och eftermarknad.",
      },
    ],
    faqs: [
      {
        q: "Vilka växelriktare fungerar med AI-styrning?",
        a: "Vi stödjer Solinteg och Sunwoda.",
      },
      {
        q: "Måste jag byta växelriktare?",
        a: "I de flesta fall inte. Intelligence ansluter till din befintliga utrustning.",
      },
      {
        q: "Vad skiljer det från manuella scheman i växelriktarens app?",
        a: "I stället för fasta tider väger Intelligence flera faktorer samtidigt och justerar i realtid när väder, last och priser ändras.",
      },
    ],
    audience: "both",
  },
  {
    slug: "ai-styrning-batteri",
    title: "AI-styrning för batteri & hembatteri | Samvolt Intelligence",
    description:
      "AI-styrning för ditt batteri. Samvolt Intelligence optimerar laddning och urladdning utifrån hela anläggningen – sol, last, effekt och mer. Stöd för Solinteg och Sunwoda.",
    h1: "AI-styrning för ditt",
    h1Accent: "batteri",
    lead: "Ett batteri som bara laddas och urladdas efter enkla regler lämnar värde på bordet. Samvolt Intelligence styr batteriet som del av hela anläggningen – automatiskt och i realtid.",
    sections: [
      {
        heading: "Batteriet i ett större sammanhang",
        body: "AI:n tar hänsyn till solproduktion, husets förbrukning, batterinivå, elpris och effekttoppar. Resultatet är en drift som maximerar nyttan av varje sparad kilowattimme.",
      },
      {
        heading: "Hembatteri med Solinteg eller Sunwoda",
        body: "Koppla ditt batteri via stödda växelriktare och få övervakning plus AI-styrning i samma vy – utan att du behöver sitta och styra schemat själv.",
      },
      {
        heading: "Sälj in batteri med mer värde",
        body: "För installatörer och sälj: AI-styrning gör batteriaffären starkare. Kunden får mer ut av investeringen, ni får eftermarknad och tydligare merförsäljning.",
      },
    ],
    faqs: [
      {
        q: "Kan jag styra mitt hembatteri med AI?",
        a: "Ja. Intelligence planerar och justerar batteridriften automatiskt utifrån hela anläggningens behov.",
      },
      {
        q: "Fungerar det med mitt batteri?",
        a: "Om batteriet sitter bakom en stödd växelriktare – till exempel Solinteg eller Sunwoda – kan du normalt ansluta via Samvolt Intelligence.",
      },
      {
        q: "Hjälper det mot effekttoppar?",
        a: "Ja. Systemet kan kapa dyra effekttoppar som del av den samlade optimeringen – särskilt relevant om du har effekttariff.",
      },
    ],
    audience: "both",
  },
  {
    slug: "intelligent-energistyrning",
    title: "Intelligent energistyrning för sol & batteri | Samvolt",
    description:
      "Intelligent energistyrning med AI: förutse, optimera och styr sol, batteri och förbrukning i realtid. För privatpersoner, installatörer och sälj. Solinteg & Sunwoda.",
    h1: "Intelligent",
    h1Accent: "energistyrning",
    lead: "Samvolt Intelligence är plattformen som får sol, batteri och förbrukning att arbeta som ett system – med AI som förutser, optimerar och styr i realtid.",
    sections: [
      {
        heading: "Vad intelligent energistyrning betyder",
        body: "Inte en enskild automatisering, utan kontinuerlig drift av hela anläggningen: prognoser, batteribeslut, egenanvändning, effekt och uppföljning i samma flöde.",
      },
      {
        heading: "Privatpersoner",
        body: "Se och förstå din energi, låt AI:n sköta driften, och få mer värde ur sol och batteri utan daglig finjustering.",
      },
      {
        heading: "Installatörer och sälj",
        body: "Erbjud energistyrning efter installationen. Övervaka portföljen, minska supportärenden och hitta när kunder behöver mer sol, batteri eller laddning.",
      },
    ],
    faqs: [
      {
        q: "Vad är skillnaden mot att bara följa elpriset?",
        a: "Elpris är en av flera ingångar. Intelligence väger också solprognos, förbrukning, batterinivå och effekttoppar – och justerar i realtid.",
      },
      {
        q: "Vilken utrustning stöds?",
        a: "Växelriktare och batterier från Solinteg och Sunwoda.",
      },
      {
        q: "Vem är det för?",
        a: "Både privatpersoner med sol och batteri, och installatörer/sälj som vill ge kunderna AI-styrning och bygga eftermarknad.",
      },
    ],
    audience: "both",
  },
];

export function getSeoPage(slug: string): SeoPageContent | undefined {
  return SEO_PAGES.find((p) => p.slug === slug);
}

export const HOME_FAQS = [
  {
    q: "Vad är Samvolt Intelligence?",
    a: "En AI-driven plattform som förutser, optimerar och styr ditt hems energiflöden i realtid – så att sol, batteri och förbrukning samspelar och varje kilowattimme används så smart som möjligt.",
  },
  {
    q: "Fungerar det med Solinteg och Sunwoda?",
    a: "Ja. Intelligence kopplas till växelriktare och batterier från Solinteg och Sunwoda.",
  },
  {
    q: "Kan jag styra min växelriktare och mitt batteri med AI?",
    a: "Ja. Samvolt Intelligence använder AI för att optimera hela energisystemet – solproduktion, växelriktare och batteri samverkar automatiskt utifrån väder, elpris, förbrukning och dina energibehov.",
  },
  {
    q: "Behöver jag installera ny hårdvara?",
    a: "Nej. Samvolt Intelligence ansluter direkt till din kompatibla energianläggning. Ingen extra hårdvara behövs.",
  },
  {
    q: "Hur sparar jag pengar med Samvolt Intelligence?",
    a: "Samvolt Intelligence analyserar solproduktion, elpris, förbrukning och batterinivå i realtid för att använda energin när den ger störst värde. AI:n laddar, lagrar och förbrukar el vid rätt tidpunkt, minskar effekttoppar och maximerar användningen av din egen solel – vilket kan sänka både el- och nätkostnaderna.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Testa kostnadsfritt",
  },
  {
    q: "Är min data säker?",
    a: "All data hanteras på svenska servrar enligt europeiska lagar och regler, inklusive GDPR. Inloggning sker med BankID, och plattformen är utvecklad i Sverige med dataskydd i fokus.",
  },
];

export const HOME_FAQS_EN = [
  {
    q: "What is Samvolt Intelligence?",
    a: "An AI-driven platform that predicts, optimizes and controls your home's energy flows in real time – so that solar, battery and consumption work together and every kilowatt-hour is used as smartly as possible.",
  },
  {
    q: "Does it work with Solinteg and Sunwoda?",
    a: "Yes. Intelligence connects to inverters and batteries from Solinteg and Sunwoda.",
  },
  {
    q: "Can I control my inverter and battery with AI?",
    a: "Yes. Samvolt Intelligence uses AI to optimize your entire energy system – solar production, inverter and battery work together automatically based on weather, electricity prices, consumption and your energy needs.",
  },
  {
    q: "Do I need to install new hardware?",
    a: "No. Samvolt Intelligence connects directly to your compatible energy installation. No extra hardware is needed.",
  },
  {
    q: "How do I save money with Samvolt Intelligence?",
    a: "Samvolt Intelligence analyzes solar production, electricity prices, consumption and battery level in real time to use energy when it delivers the most value. The AI charges, stores and consumes electricity at the right time, reduces power peaks and maximizes the use of your own solar energy – which can lower both electricity and grid costs.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Try for free",
  },
  {
    q: "Is my data safe?",
    a: "All data is handled on Swedish servers in accordance with European laws and regulations, including GDPR. Sign-in is through BankID, and the platform is developed in Sweden with data protection in focus.",
  },
];

type SiteMeta = { title: string; description: string; keywords: string[] };

const SITE_META: Record<Locale, SiteMeta> = {
  sv: {
    title: SITE_TITLE,
    description: SITE_DESCRIPTION,
    keywords: SITE_KEYWORDS,
  },
  en: {
    title: SITE_TITLE_EN,
    description: SITE_DESCRIPTION_EN,
    keywords: SITE_KEYWORDS_EN,
  },
  no: {
    title:
      "AI-styring for sol og batteri | Solinteg og Sunwoda | Samvolt Intelligence",
    description:
      "AI-styring for sol, batteri og vekselretter. Samvolt Intelligence forutser, optimaliserer og styrer hele anlegget i sanntid – støtter Solinteg og Sunwoda. For privatpersoner, installatører og salg.",
    keywords: [
      "AI energistyring",
      "AI-styring solcellepaneler",
      "AI batteristyring",
      "AI vekselretterstyring",
      "AI-styring Sunwoda",
      "AI-styring Solinteg",
      "intelligent energistyring",
      "automatisk energioptimalisering",
      "energioptimalisering",
      "smart energistyring",
      "solenergi",
      "solcellepaneler",
      "vekselretter",
      "batterilagring",
      "hjemmebatteri",
      "Solinteg",
      "Sunwoda",
      "eget solforbruk",
      "effekttopper",
      "effekttariff",
      "energistyring hjemme",
      "solcelle ettermarked",
      "fjernovervåking solceller",
      "AI for installatører",
      "Samvolt",
      "Samvolt Intelligence",
    ],
  },
  da: {
    title:
      "AI-styring til sol og batteri | Solinteg og Sunwoda | Samvolt Intelligence",
    description:
      "AI-styring til sol, batteri og inverter. Samvolt Intelligence forudser, optimerer og styrer hele anlægget i realtid – understøtter Solinteg og Sunwoda. Til privatpersoner, installatører og salg.",
    keywords: [
      "AI energistyring",
      "AI-styring solceller",
      "AI batteristyring",
      "AI inverterstyring",
      "AI-styring Sunwoda",
      "AI-styring Solinteg",
      "intelligent energistyring",
      "automatisk energioptimering",
      "energioptimering",
      "smart energistyring",
      "solenergi",
      "solceller",
      "inverter",
      "batterilagring",
      "hjemmebatteri",
      "Solinteg",
      "Sunwoda",
      "eget solforbrug",
      "effekttoppe",
      "effekttarif",
      "energistyring i hjemmet",
      "solcelle eftermarked",
      "fjernovervågning af solceller",
      "AI til installatører",
      "Samvolt",
      "Samvolt Intelligence",
    ],
  },
  fi: {
    title:
      "Aurinko- ja akkujärjestelmän tekoälyohjaus | Solinteg ja Sunwoda | Samvolt Intelligence",
    description:
      "Tekoälyohjaus aurinkopaneeleille, akulle ja invertterille. Samvolt Intelligence ennakoi, optimoi ja ohjaa koko järjestelmää reaaliajassa – tukee Solintegia ja Sunwodaa. Yksityishenkilöille, asentajille ja myyntiin.",
    keywords: [
      "tekoälyenergianhallinta",
      "tekoälyohjaus aurinkopaneelit",
      "tekoälyakunohjaus",
      "tekoälyohjaus invertteri",
      "tekoälyohjaus Sunwoda",
      "tekoälyohjaus Solinteg",
      "älykäs energianhallinta",
      "automaattinen energian optimointi",
      "energian optimointi",
      "älykäs energianohjaus",
      "aurinkoenergia",
      "aurinkopaneelit",
      "invertteri",
      "akkuvarasto",
      "kotiakku",
      "Solinteg",
      "Sunwoda",
      "aurinkosähkön oma käyttö",
      "tehohuiput",
      "tehotariffi",
      "kodin energianhallinta",
      "aurinkopaneelien jälkimarkkina",
      "aurinkopaneelien etävalvonta",
      "tekoäly asentajille",
      "Samvolt",
      "Samvolt Intelligence",
    ],
  },
  de: {
    title:
      "KI-Steuerung für Solar & Batterie | Solinteg & Sunwoda | Samvolt Intelligence",
    description:
      "KI-Steuerung für Solar, Batterie und Wechselrichter. Samvolt Intelligence prognostiziert, optimiert und steuert Ihre gesamte Anlage in Echtzeit – unterstützt Solinteg und Sunwoda. Für Privatpersonen, Installateure und Vertrieb.",
    keywords: [
      "KI-Energiemanagement",
      "KI-Steuerung Solarmodule",
      "KI-Batteriesteuerung",
      "KI-Wechselrichtersteuerung",
      "KI-Steuerung Sunwoda",
      "KI-Steuerung Solinteg",
      "intelligentes Energiemanagement",
      "automatische Energieoptimierung",
      "Energieoptimierung",
      "smartes Energiemanagement",
      "Solarenergie",
      "Solarmodule",
      "Wechselrichter",
      "Batteriespeicher",
      "Heimbatterie",
      "Solinteg",
      "Sunwoda",
      "Solar-Eigenverbrauch",
      "Lastspitzen",
      "Leistungstarif",
      "Energiemanagement zu Hause",
      "Solar-Aftermarket",
      "Solar-Fernüberwachung",
      "KI für Installateure",
      "Samvolt",
      "Samvolt Intelligence",
    ],
  },
  cs: {
    title:
      "AI řízení pro solár a baterii | Solinteg a Sunwoda | Samvolt Intelligence",
    description:
      "AI řízení pro solární panely, baterii a střídač. Samvolt Intelligence předpovídá, optimalizuje a řídí celou instalaci v reálném čase – podporuje Solinteg a Sunwoda. Pro domácnosti, instalatéry a prodej.",
    keywords: [
      "AI energetický management",
      "AI řízení solárních panelů",
      "AI řízení baterie",
      "AI řízení střídače",
      "AI řízení Sunwoda",
      "AI řízení Solinteg",
      "inteligentní energetický management",
      "automatická optimalizace energie",
      "optimalizace energie",
      "chytrý energetický management",
      "solární energie",
      "solární panely",
      "střídač",
      "bateriové úložiště",
      "domácí baterie",
      "Solinteg",
      "Sunwoda",
      "vlastní spotřeba solární energie",
      "špičky výkonu",
      "výkonový tarif",
      "energetický management domácnosti",
      "solární aftermarket",
      "vzdálené monitorování solárních panelů",
      "AI pro instalatéry",
      "Samvolt",
      "Samvolt Intelligence",
    ],
  },
  zh: {
    title:
      "太阳能与电池的 AI 控制 | Solinteg 与 Sunwoda | Samvolt Intelligence",
    description:
      "面向太阳能、电池和逆变器的 AI 控制。Samvolt Intelligence 实时预测、优化并控制您的整个系统——支持 Solinteg 和 Sunwoda。适用于家庭用户、安装商和销售。",
    keywords: [
      "AI 能源管理",
      "AI 控制太阳能板",
      "AI 电池控制",
      "AI 逆变器控制",
      "AI 控制 Sunwoda",
      "AI 控制 Solinteg",
      "智能能源管理",
      "自动能源优化",
      "能源优化",
      "智慧能源管理",
      "太阳能",
      "太阳能板",
      "逆变器",
      "电池储能",
      "家用电池",
      "Solinteg",
      "Sunwoda",
      "太阳能自用",
      "功率峰值",
      "功率电价",
      "家庭能源管理",
      "太阳能售后市场",
      "太阳能远程监控",
      "面向安装商的 AI",
      "Samvolt",
      "Samvolt Intelligence",
    ],
  },
};

export function siteMeta(locale: Locale): SiteMeta {
  return SITE_META[locale] ?? SITE_META.sv;
}

type HomeFaq = { q: string; a: string; linkHref?: string; linkLabel?: string };

const HOME_FAQS_NO: HomeFaq[] = [
  {
    q: "Hva er Samvolt Intelligence?",
    a: "En AI-drevet plattform som forutser, optimaliserer og styrer hjemmets energiflyt i sanntid – slik at sol, batteri og forbruk spiller sammen og hver kilowattime brukes så smart som mulig.",
  },
  {
    q: "Fungerer det med Solinteg og Sunwoda?",
    a: "Ja. Intelligence kobles til vekselrettere og batterier fra Solinteg og Sunwoda.",
  },
  {
    q: "Kan jeg styre vekselretteren og batteriet mitt med AI?",
    a: "Ja. Samvolt Intelligence bruker AI for å optimalisere hele energisystemet – solproduksjon, vekselretter og batteri samarbeider automatisk basert på vær, strømpris, forbruk og dine energibehov.",
  },
  {
    q: "Må jeg installere ny maskinvare?",
    a: "Nei. Samvolt Intelligence kobles direkte til ditt kompatible energianlegg. Ingen ekstra maskinvare er nødvendig.",
  },
  {
    q: "Hvordan sparer jeg penger med Samvolt Intelligence?",
    a: "Samvolt Intelligence analyserer solproduksjon, strømpris, forbruk og batterinivå i sanntid for å bruke energien når den gir størst verdi. AI-en lader, lagrer og forbruker strøm til rett tid, reduserer effekttopper og maksimerer bruken av din egen solenergi – noe som kan senke både strøm- og nettkostnader.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Prøv gratis",
  },
  {
    q: "Er dataene mine trygge?",
    a: "Alle data håndteres på svenske servere i samsvar med europeiske lover og regler, inkludert GDPR. Innlogging skjer med BankID, og plattformen er utviklet i Sverige med databeskyttelse i fokus.",
  },
];

const HOME_FAQS_DA: HomeFaq[] = [
  {
    q: "Hvad er Samvolt Intelligence?",
    a: "En AI-drevet platform, der forudser, optimerer og styrer dit hjems energistrømme i realtid – så sol, batteri og forbrug spiller sammen, og hver kilowatt-time bruges så smart som muligt.",
  },
  {
    q: "Virker det med Solinteg og Sunwoda?",
    a: "Ja. Intelligence forbindes til invertere og batterier fra Solinteg og Sunwoda.",
  },
  {
    q: "Kan jeg styre min inverter og mit batteri med AI?",
    a: "Ja. Samvolt Intelligence bruger AI til at optimere hele dit energisystem – solproduktion, inverter og batteri arbejder sammen automatisk baseret på vejr, elpriser, forbrug og dine energibehov.",
  },
  {
    q: "Skal jeg installere ny hardware?",
    a: "Nej. Samvolt Intelligence forbindes direkte til dit kompatible energianlæg. Der kræves ingen ekstra hardware.",
  },
  {
    q: "Hvordan sparer jeg penge med Samvolt Intelligence?",
    a: "Samvolt Intelligence analyserer solproduktion, elpriser, forbrug og batteriniveau i realtid for at bruge energien, når den giver størst værdi. AI'en oplader, lagrer og forbruger el på det rette tidspunkt, reducerer effekttoppe og maksimerer brugen af din egen solenergi – hvilket kan sænke både el- og netomkostninger.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Prøv gratis",
  },
  {
    q: "Er mine data sikre?",
    a: "Alle data håndteres på svenske servere i overensstemmelse med europæiske love og regler, herunder GDPR. Login sker med BankID, og platformen er udviklet i Sverige med databeskyttelse i fokus.",
  },
];

const HOME_FAQS_FI: HomeFaq[] = [
  {
    q: "Mikä on Samvolt Intelligence?",
    a: "Tekoälypohjainen alusta, joka ennakoi, optimoi ja ohjaa kotisi energiavirtoja reaaliajassa – niin että aurinko, akku ja kulutus toimivat yhdessä ja jokainen kilowattitunti käytetään mahdollisimman fiksusti.",
  },
  {
    q: "Toimiiko se Solintegin ja Sunwodan kanssa?",
    a: "Kyllä. Intelligence yhdistyy Solintegin ja Sunwodan inverttereihin ja akkuihin.",
  },
  {
    q: "Voinko ohjata invertteriäni ja akkuani tekoälyllä?",
    a: "Kyllä. Samvolt Intelligence käyttää tekoälyä koko energiajärjestelmäsi optimointiin – aurinkotuotanto, invertteri ja akku toimivat automaattisesti yhdessä sään, sähkön hinnan, kulutuksen ja energiantarpeidesi perusteella.",
  },
  {
    q: "Täytyykö minun asentaa uutta laitteistoa?",
    a: "Ei. Samvolt Intelligence yhdistyy suoraan yhteensopivaan energiajärjestelmääsi. Ylimääräistä laitteistoa ei tarvita.",
  },
  {
    q: "Miten säästän rahaa Samvolt Intelligencen avulla?",
    a: "Samvolt Intelligence analysoi aurinkotuotantoa, sähkön hintaa, kulutusta ja akun tasoa reaaliajassa käyttääkseen energian silloin, kun se tuottaa eniten arvoa. Tekoäly lataa, varastoi ja kuluttaa sähköä oikeaan aikaan, vähentää tehohuippuja ja maksimoi oman aurinkosähkösi käytön – mikä voi laskea sekä sähkö- että verkkokustannuksia.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Kokeile ilmaiseksi",
  },
  {
    q: "Ovatko tietoni turvassa?",
    a: "Kaikki tiedot käsitellään ruotsalaisilla palvelimilla eurooppalaisten lakien ja säännösten, mukaan lukien GDPR:n, mukaisesti. Kirjautuminen tapahtuu BankID:llä, ja alusta on kehitetty Ruotsissa tietosuoja edellä.",
  },
];

const HOME_FAQS_DE: HomeFaq[] = [
  {
    q: "Was ist Samvolt Intelligence?",
    a: "Eine KI-gestützte Plattform, die die Energieflüsse Ihres Zuhauses in Echtzeit prognostiziert, optimiert und steuert – damit Solar, Batterie und Verbrauch zusammenspielen und jede Kilowattstunde so intelligent wie möglich genutzt wird.",
  },
  {
    q: "Funktioniert es mit Solinteg und Sunwoda?",
    a: "Ja. Intelligence verbindet sich mit Wechselrichtern und Batterien von Solinteg und Sunwoda.",
  },
  {
    q: "Kann ich meinen Wechselrichter und meine Batterie mit KI steuern?",
    a: "Ja. Samvolt Intelligence nutzt KI, um Ihr gesamtes Energiesystem zu optimieren – Solarproduktion, Wechselrichter und Batterie arbeiten automatisch zusammen, basierend auf Wetter, Strompreisen, Verbrauch und Ihrem Energiebedarf.",
  },
  {
    q: "Muss ich neue Hardware installieren?",
    a: "Nein. Samvolt Intelligence verbindet sich direkt mit Ihrer kompatiblen Energieanlage. Es ist keine zusätzliche Hardware erforderlich.",
  },
  {
    q: "Wie spare ich mit Samvolt Intelligence Geld?",
    a: "Samvolt Intelligence analysiert Solarproduktion, Strompreise, Verbrauch und Batteriestand in Echtzeit, um Energie dann zu nutzen, wenn sie den größten Wert bringt. Die KI lädt, speichert und verbraucht Strom zum richtigen Zeitpunkt, reduziert Lastspitzen und maximiert die Nutzung Ihrer eigenen Solarenergie – was sowohl Strom- als auch Netzkosten senken kann.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Kostenlos testen",
  },
  {
    q: "Sind meine Daten sicher?",
    a: "Alle Daten werden auf schwedischen Servern gemäß europäischen Gesetzen und Vorschriften, einschließlich DSGVO, verarbeitet. Die Anmeldung erfolgt über BankID, und die Plattform wurde in Schweden mit Fokus auf Datenschutz entwickelt.",
  },
];

const HOME_FAQS_CS: HomeFaq[] = [
  {
    q: "Co je Samvolt Intelligence?",
    a: "AI platforma, která předpovídá, optimalizuje a řídí energetické toky vaší domácnosti v reálném čase – tak, aby solár, baterie a spotřeba spolupracovaly a každá kilowatthodina byla využita co nejchytřeji.",
  },
  {
    q: "Funguje to se Solinteg a Sunwoda?",
    a: "Ano. Intelligence se připojuje ke střídačům a bateriím od Solinteg a Sunwoda.",
  },
  {
    q: "Mohu řídit svůj střídač a baterii pomocí AI?",
    a: "Ano. Samvolt Intelligence využívá AI k optimalizaci celého vašeho energetického systému – solární výroba, střídač a baterie spolupracují automaticky na základě počasí, cen elektřiny, spotřeby a vašich energetických potřeb.",
  },
  {
    q: "Musím instalovat nový hardware?",
    a: "Ne. Samvolt Intelligence se připojuje přímo k vaší kompatibilní energetické instalaci. Žádný další hardware není potřeba.",
  },
  {
    q: "Jak s Samvolt Intelligence ušetřím peníze?",
    a: "Samvolt Intelligence analyzuje solární výrobu, ceny elektřiny, spotřebu a stav baterie v reálném čase, aby energii využila tehdy, když přináší největší hodnotu. AI nabíjí, ukládá a spotřebovává elektřinu ve správný čas, snižuje špičky výkonu a maximalizuje využití vaší vlastní solární energie – což může snížit náklady na elektřinu i na síť.",
    linkHref: APP_TRIAL_URL,
    linkLabel: "Vyzkoušet zdarma",
  },
  {
    q: "Jsou má data v bezpečí?",
    a: "Veškerá data jsou zpracovávána na švédských serverech v souladu s evropskými zákony a předpisy, včetně GDPR. Přihlášení probíhá přes BankID a platforma je vyvinuta ve Švédsku s důrazem na ochranu dat.",
  },
];

const HOME_FAQS_ZH: HomeFaq[] = [
  {
    q: "什么是 Samvolt Intelligence？",
    a: "一个由 AI 驱动的平台，实时预测、优化并控制您家庭的能源流动——让太阳能、电池和用电协同工作，使每一度电都尽可能被智能地利用。",
  },
  {
    q: "它支持 Solinteg 和 Sunwoda 吗？",
    a: "支持。Intelligence 可连接 Solinteg 和 Sunwoda 的逆变器和电池。",
  },
  {
    q: "我可以用 AI 控制我的逆变器和电池吗？",
    a: "可以。Samvolt Intelligence 使用 AI 优化您的整个能源系统——太阳能发电、逆变器和电池会根据天气、电价、用电量和您的能源需求自动协同工作。",
  },
  {
    q: "我需要安装新硬件吗？",
    a: "不需要。Samvolt Intelligence 可直接连接到您兼容的能源系统。无需额外硬件。",
  },
  {
    q: "我如何通过 Samvolt Intelligence 省钱？",
    a: "Samvolt Intelligence 实时分析太阳能发电、电价、用电量和电池电量，在能源价值最高时加以利用。AI 会在合适的时间充电、储能和用电，削减功率峰值，并最大化利用您自己的太阳能——这可以降低电费和电网费用。",
    linkHref: APP_TRIAL_URL,
    linkLabel: "免费试用",
  },
  {
    q: "我的数据安全吗？",
    a: "所有数据均在瑞典服务器上处理，符合包括 GDPR 在内的欧洲法律法规。通过 BankID 登录，平台在瑞典开发，注重数据保护。",
  },
];

const HOME_FAQS_BY_LOCALE: Record<Locale, HomeFaq[]> = {
  sv: HOME_FAQS,
  en: HOME_FAQS_EN,
  no: HOME_FAQS_NO,
  da: HOME_FAQS_DA,
  fi: HOME_FAQS_FI,
  de: HOME_FAQS_DE,
  cs: HOME_FAQS_CS,
  zh: HOME_FAQS_ZH,
};

export function homeFaqs(locale: Locale): HomeFaq[] {
  return HOME_FAQS_BY_LOCALE[locale] ?? HOME_FAQS;
}

export function organizationJsonLd(locale: Locale = "sv") {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/brand/icon.svg`,
    description: siteMeta(locale).description,
    areaServed: "SE",
  };
}

export function softwareJsonLd(locale: Locale = "sv") {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    description: siteMeta(locale).description,
    url: SITE_URL,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "SEK",
      description: locale === "en" ? "Free trial" : "Kostnadsfri provperiod",
    },
    audience: {
      "@type": "Audience",
      audienceType:
        locale === "en"
          ? "Homeowners, installers and sales"
          : "Privatpersoner, installatörer och sälj",
    },
  };
}

export function faqJsonLd(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}
